import { useContext, useMemo, useRef, useState } from 'react';
import { Plural, Trans } from '@lingui/react/macro';
import { useHistory } from 'react-router';
import useResourceLoader from '../../util/useResourceLoader';
import { Button, Dropdown, DropdownMenu, DropdownToggle, UncontrolledTooltip } from 'src/components/ui';
import Loader from '../Loader';
import { AuthContext } from '../../context/AuthContext';
import UWEEntityApi from '../../api/UWEEntityApi';
import { UWEEntity } from '../../api/types';
import dayjs, { Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Icon from 'src/components/Icon';
import classnames from 'src/util/classnames';

dayjs.extend(localizedFormat);

const REPORT_SUBMISSION_PATH = '/workflow/report-submission';
const DUE_SOON_DAYS = 7;

const reportPath = (reportId: number) => `${REPORT_SUBMISSION_PATH}/${reportId}`;

const fetchReportsForWindows = async (orgId: number, windowIds: number[], abortController?: AbortController): Promise<UWEEntity[]> => {
  const reportsReq = await UWEEntityApi.getWorkTray({
    resource: `uwe-entities/Report/list/${orgId}`,
    offset: 0,
    size: 10000,
    sortBy: 'id',
    closedStatus: 'both',
    'metadata-in-reportingWindowId': windowIds.join(','),
    abortController,
  });

  // Re-check the window id client-side so an ignored/unsupported filter can never make us treat
  // an unrelated report as "the" report for a window.
  const requestedIds = new Set(windowIds.map(String));
  return (reportsReq?.items ?? []).filter(report => requestedIds.has(String(report.metadata?.reportingWindowId)));
};

const byDueDate = (a: UWEEntity, b: UWEEntity) => dayjs(a.metadata?.dueDate).valueOf() - dayjs(b.metadata?.dueDate).valueOf();

/**
 * Reads a count from list-API metadata (the list DTO carries no `data`): an explicit numeric
 * `countKey` if the schema's metadata map defines one, else the length of a JSON-array `listKey`.
 * Returns null when neither is present so the caller can omit the line entirely.
 */
const countFromMetadata = (metadata: Record<string, string> | undefined, countKey: string, listKey: string): number | null => {
  const explicitCount = Number(metadata?.[countKey]);
  if (metadata?.[countKey] && Number.isFinite(explicitCount)) return explicitCount;

  try {
    const list = JSON.parse(metadata?.[listKey] ?? 'null');
    return Array.isArray(list) ? list.length : null;
  } catch {
    return null;
  }
};

const NewReportBtn = () => {
  const auth = useContext(AuthContext);
  const history = useHistory();

  const [startingWindow, setStartingWindow] = useState<UWEEntity | null>(null);
  const [errorOnCreate, setErrorOnCreate] = useState<string | null>(null);
  // Synchronous guard against double-submits: state updates are async, a ref is not.
  const startInFlight = useRef(false);

  const orgId = useMemo(() => {
    return auth?.user?.user?.actualOrganizationalUnitId;
    // or auth.user.user.organizationalUnit.id ?? is there a difference??
  }, [auth]);

  const startReport = async (reportingWindow: UWEEntity) => {
    if (!orgId || startInFlight.current) return;
    startInFlight.current = true;
    setStartingWindow(reportingWindow);

    try {
      // The open-windows list may be stale (another user can start the report after this page
      // loaded), and a window has exactly one report — so re-check right before creating.
      const [existingReport] = await fetchReportsForWindows(orgId, [reportingWindow.id]);
      if (existingReport) {
        history.push(reportPath(existingReport.id));
        return;
      }

      const workflow = await UWEEntityApi.getWorkflowInfoForSlug({ slug: 'report-submission' });
      const wfAccessToken = await UWEEntityApi.createWfAccessTokenHash({
        workflowId: workflow.id,
        uweEntityId: workflow.objectSchemaId,
        canView: true,
        canCreate: true,
        canUpdate: true,
      });

      const newReport = await UWEEntityApi.createUWEEntity({
        page: 'FFEIC-031', // TODO rename this form?...
        formData: {
          action: 'save',
          comments: [],
          entityTypeId: workflow.objectSchemaId,
          data: { reportingWindow: reportingWindow.id },
          organizationalUnit: { id: orgId },
        },
        wfAccessToken,
      });

      if (newReport.success) {
        history.push(reportPath(newReport.data.id));
      } else {
        setErrorOnCreate(newReport.error);
      }
    } catch (e) {
      console.error(e);
      setErrorOnCreate(e instanceof Error ? e.message : String(e));
    } finally {
      startInFlight.current = false;
      setStartingWindow(null);
    }
  };

  const [openWindows, loading = true, errorLoadingWindows] = useResourceLoader(
    async abortController => {
      // fetch reporting windows that are open
      // fetch reports for those windows
      // return open windows with no started reports
      if (!orgId) return [];

      const today = dayjs();

      try {
        const reportingWindowsReq = await UWEEntityApi.getWorkTray({
          resource: `uwe-entities/Reporting%20Window/list/${orgId}`,
          offset: 0,
          size: 10000,
          sortBy: 'startDate',
          isOpen: true,
          abortController,
        });

        if (reportingWindowsReq?.items?.length === 0) return [];

        const reportingWindows = reportingWindowsReq.items.filter(rw => {
          if (rw.organizationalUnit.id !== orgId) return false; // filter out parent orgs, just want the actual institution windows

          const { endDate, dueDate } = rw.metadata;
          const endsAt = dayjs(endDate);
          const dueAt = dayjs(dueDate);
          return endsAt.isBefore(today) && (dueAt.isAfter(today) || dueAt.isSame(today, 'day'));
        });

        const windowIds = reportingWindows.map(rw => rw.id);

        if (windowIds?.length === 0) return [];

        const reports = await fetchReportsForWindows(orgId, windowIds, abortController);
        const reportedWindowIds = new Set(reports.map(r => r.metadata?.reportingWindowId));

        return reportingWindows.filter(rw => !reportedWindowIds.has(String(rw.id))).sort(byDueDate);
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') throw e;
        console.error('Error loading open reporting windows...');
        throw e;
      }
    },
    [orgId]
  );

  if (loading) {
    return (
      <Loader className="white" sm inline size={15}>
        <Trans>Checking current reporting status</Trans>
      </Loader>
    );
  }

  if (startingWindow) {
    const periodIdentifier = startingWindow.metadata?.periodIdentifier;
    return (
      <Loader className="white" spinner sm inline size={20}>
        <Trans>Opening report for {periodIdentifier}</Trans>
      </Loader>
    );
  }

  if (errorLoadingWindows || errorOnCreate) {
    return (
      <div>
        <Icon icon="Warning" className="text-danger" id="ocif-home-workpage-new-report-btn" style={{ cursor: 'pointer', fontSize: '1.5em' }} />
        <UncontrolledTooltip placement="top" target="ocif-home-workpage-new-report-btn" fade={false}>
          {errorOnCreate ? (
            <Trans>The report could not be started. Refresh the page and try again. If the problem persists, contact a system administrator.</Trans>
          ) : (
            <Trans>
              There was an error loading available reports. Please refresh the page. If problem persists, please contact a system administrator.
            </Trans>
          )}
        </UncontrolledTooltip>
      </div>
    );
  }

  if (!openWindows || openWindows.length === 0) {
    return null;
  }

  if (openWindows.length > 1) {
    return <ReportingWindowPicker windows={openWindows} onSelect={startReport} />;
  }

  // Just one open window
  const [onlyWindow] = openWindows;
  return (
    <Button
      size="sm"
      title={onlyWindow.metadata?.periodIdentifier}
      className="flex items-center gap-1"
      color="warning"
      onClick={() => {
        startReport(onlyWindow);
      }}
    >
      <Icon icon="Plus" weight="regular" />
      <Trans>Start Report</Trans> <small>({onlyWindow.metadata?.periodIdentifier})</small>
    </Button>
  );
};

type DueTone = 'urgent' | 'soon' | 'later';

const DUE_TONE_CLASSES: Record<DueTone, string> = {
  urgent: 'bg-danger/10 text-danger',
  soon: 'bg-warning/15 text-ink',
  later: 'bg-surface-2 text-ink-2',
};

const DueChip = ({ dueDate, today }: { dueDate: string; today: Dayjs }) => {
  const dueAt = dayjs(dueDate);
  const daysLeft = dueAt.startOf('day').diff(today.startOf('day'), 'day');
  const tone: DueTone = daysLeft <= 1 ? 'urgent' : daysLeft <= DUE_SOON_DAYS ? 'soon' : 'later';
  const dueLabel = dueAt.format('ll');

  return (
    <span
      className={classnames(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        DUE_TONE_CLASSES[tone]
      )}
    >
      {tone !== 'later' && <Icon icon="Clock" size={12} aria-hidden />}
      <Trans>Due: {dueLabel}</Trans>
    </span>
  );
};

const ReportingWindowPicker = ({ windows, onSelect }: { windows: UWEEntity[]; onSelect: (reportingWindow: UWEEntity) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const today = dayjs();
  const windowCount = windows.length;

  const close = () => {
    setIsOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)}>
      <DropdownToggle ref={toggleRef} caret={false} size="sm" color="warning" aria-haspopup="menu" className="flex items-center gap-1.5">
        <Icon icon="Plus" weight="regular" size={16} aria-hidden />
        <Trans>Start Report</Trans>
        <span className="rounded-full bg-black/10 px-1.5 text-xs leading-5 font-semibold tabular-nums">{windowCount}</span>
        <Icon
          icon="CaretDown"
          size={14}
          aria-hidden
          className={classnames('transition-transform duration-150 motion-reduce:transition-none', isOpen && 'rotate-180')}
        />
      </DropdownToggle>

      <DropdownMenu
        container="body"
        role="menu"
        className="w-[30rem] overflow-hidden rounded-xl! p-0! shadow-lg"
        onKeyDown={e => {
          if (e.key === 'Escape') close();
        }}
      >
        <li className="border-b border-line px-4 pt-3 pb-2.5" role="presentation">
          <div className="text-sm font-semibold text-ink">
            <Trans>Choose a reporting period</Trans>
          </div>
          <div className="text-xs text-ink-3">
            <Trans>{windowCount} periods are open. The one due soonest is listed first.</Trans>
          </div>
        </li>

        <li role="presentation" className="max-h-80 overflow-y-auto">
          <ul className="m-0 list-none p-1.5">
            {windows.map((rw, index) => {
              const { periodIdentifier, startDate, endDate, dueDate } = rw.metadata ?? {};
              const coverageStart = dayjs(startDate).format('ll');
              const coverageEnd = dayjs(endDate).format('ll');
              const scheduleCount = countFromMetadata(rw.metadata, 'scheduleCount', 'scheduleTitles');
              const inventoryScheduleCount = countFromMetadata(rw.metadata, 'inventoryScheduleCount', 'inventorySchedules');

              return (
                <li key={rw.id} role="presentation">
                  <button
                    type="button"
                    role="menuitem"
                    autoFocus={index === 0}
                    onClick={() => {
                      setIsOpen(false);
                      onSelect(rw);
                    }}
                    className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors duration-100 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-ink-2 group-hover:bg-warning/20 group-hover:text-ink group-focus-visible:bg-warning/20">
                      <Icon icon="FilePlus" size={16} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">{periodIdentifier}</span>
                      {startDate && endDate && (
                        <span className="flex items-center gap-1 text-xs text-ink-3 italic">
                          {coverageStart}
                          <Icon icon="ArrowRight" size={12} aria-hidden className="shrink-0" />
                          <span className="sr-only">
                            <Trans>to</Trans>
                          </span>
                          {coverageEnd}
                        </span>
                      )}
                      {(!!scheduleCount || !!inventoryScheduleCount) && (
                        <span className="block text-xs text-ink-3">
                          {!!scheduleCount && <Plural value={scheduleCount} one="# schedule" other="# schedules" />}
                          {!!scheduleCount && !!inventoryScheduleCount && ', '}
                          {!!inventoryScheduleCount && (
                            <Plural value={inventoryScheduleCount} one="# inventory schedule" other="# inventory schedules" />
                          )}
                        </span>
                      )}
                    </span>
                    {dueDate && <DueChip dueDate={dueDate} today={today} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </li>

        <li role="presentation" className="flex items-start gap-2 border-t border-line bg-surface-2 px-4 py-2.5 text-xs text-ink-3">
          <Icon icon="Info" size={14} aria-hidden className="mt-px shrink-0" />
          <Trans>Each period has a single report. If a colleague already started it, you'll be taken to their report.</Trans>
        </li>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NewReportBtn;
